import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { addHours } from 'date-fns'

import ExtendedMap from '@/models/ExtendedMap'
import { commonWorkbookService } from '@/service/commonWorkbook.service'
import dateUtils from '@/utils/DateUtils'

import { DTO } from '../DTO'
import { SchoolGrade } from '../Student'

const now = new Date()

export class StudentWorkbook extends DTO {
  id: number //학생-교재 id
  type: res.StudentWorkbook['type'] // ALL(전부), PUBLIC(시중교재), SCHOOL(교과서), CUSTOM ( 내 교재 )
  level: common.ProblemLevel //난이도
  // page: WorkbookEntity.Workbook['page'] //총 페이지 수
  schoolGrade: SchoolGrade
  grade: common.GradeSubject
  revision: common.Revision
  semester: WorkbookEntity.Workbook['semester'] //학기
  headtitle: string // 커스텀교재 - 교재명
  subtitle: string | null // 커스텀교재 - 소제목
  title: string // 시중교재 - 책 제목 / 커스텀교재 - 학년,학기
  publisher: res.StudentWorkbook['publisher'] //출판사
  edition: res.StudentWorkbook['edition'] //판수
  autoScored: boolean // 자동채점 여부
  pairFlag: WorkbookEntity.CustomSignatureWorkbook['pairFlag'] // 쌍둥이 인지
  // pdfUrl: res.StudentWorkbook['pdfUrl'] //교재 Url -> 시중교재는 없음
  answerPdfUrl: res.StudentWorkbook['answerPdfUrl'] //빠른 정답 url
  createDate: Date //생성일
  academyId: string //생성 기관 ID
  teacherId: string //생성 선생님 ID
  progress: number // 최근 진도의 페이지 번호
  recentRevisionId: number // 최근 진도인 회차의 ID
  studentWorkbook: WorkbookEntity.WorkbookResponse['list'][number] // 학생 - 교재 정보
  recentRevisionRound: number // 최근 진도인 회차
  recentAssignedDate: Date // 최근 출제 날짜
  roundToRevisionRoundMap: ExtendedMap<
    unknown,
    {
      id: number // 회차 id
      round: number // 회차 수
      studentWorkbookId: number // 학생 - 교재 id
      percent: number // 진행도
      recentProgressId: number
      createDatetime: string // 생성일
      lastUseDatetime: string // 최근 데이터 변경일 (채점, 진도 변경)
    }
  >
  pairX: 1 | 2 | 3 | null
  featureType: WorkbookEntity.CustomSignatureWorkbook['featureType']
  securingType: WorkbookEntity.PublicWorkbook['securingType']
  originWorkbook: WorkbookEntity.CustomWorkbook['originWorkbook']
  answerConfirmed: WorkbookEntity.PublicWorkbook['answerConfirmed']

  constructor(data: res.StudentWorkbook) {
    super()
    this.id = data.id
    this.type = data.type
    this.level = data.level
    // this.page = data.page
    this.schoolGrade = new SchoolGrade({
      schoolType: data.schoolType,
      grade: Number(data.grade) as SchoolGrade['grade'],
    })
    this.grade = data.grade
    this.semester = data.semester
    this.revision = data.revision
    this.headtitle = data.headtitle
    this.title = data.title
    this.subtitle = data.subtitle
    this.publisher = data.publisher
    this.edition = data.edition
    // this.pdfUrl = data.pdfUrl
    this.answerPdfUrl = data.answerPdfUrl
    this.academyId = data.academyId
    this.teacherId = data.teacherId
    this.createDate = new Date(data.createDatetime)
    this.autoScored = data.autoScored
    this.pairFlag = data.pairFlag
    this.progress = data.recentPageNumber
    this.studentWorkbook = data.studentWorkbook
    this.recentRevisionId = data.recentRevisionId
    this.recentRevisionRound = data.recentRevisionRound
    this.recentAssignedDate = new Date(data.recentAssignedDatetime)
    this.roundToRevisionRoundMap = ExtendedMap.fromObj(data.roundToRevisionRoundMap)
    this.pairX = data.pairFlag ? data.pairX : null
    this.featureType = data.featureType
    this.securingType = data.securingType || null
    this.originWorkbook = data.originWorkbook
    this.answerConfirmed = data.answerConfirmed
  }

  get formatDate() {
    return dateUtils.toDateString(this.createDate, '.')
  }

  get isAssignedOnlyOneRound() {
    return this.roundToRevisionRoundMap.size === 1
  }

  get lastRound() {
    return this.recentRevisionRound
  }

  get isNewAssign() {
    return addHours(this.recentAssignedDate, 24) > now
  }

  get 한글학년학기과목() {
    return SchoolGrade.getKoreanWorkbookGradeSemester({
      type: this.type,
      grade: this.grade,
      semester: this.semester,
      schoolType: this.schoolGrade.schoolType,
    })
  }

  get isInspectBook() {
    return commonWorkbookService.checkInspectBook(this.id)
  }

  // 정책적으로 UI에 표시되어야 할 TITLE 값
  // subtitle의 유무에 따라서 title 또는 titel + subtitle로 노출 시킴
  get uiWorkbookTitle() {
    return `${this.title} ${this.subtitle ? `- ${this.subtitle}` : ''}`
  }

  get korAnswerConfirmed() {
    return this.answerConfirmed ? '지원' : '미지원'
  }

  get korWorkbookType() {
    switch (this.type) {
      case 'PUBLIC':
        return '시중교재'
      case 'CUSTOM':
        return '내 교재'
      case 'CUSTOM_SIGNATURE':
        return '시그니처 교재'
      case 'ACADEMY_GROUP':
        return tJosa('학원그룹교재')
      case 'SCHOOL':
        return '교과서'
      default:
        return ''
    }
  }
}
